import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { PermisoRepository } from 'app/core/domain/repositories/permiso.repository';
import { PermisoEntity, PermisoItemEntity } from 'app/core/domain/entities/permiso.entity';
import { Result, NetworkError } from 'app/core/domain/common';
import { StoragePort } from 'app/core/application/ports/storage.port';
import { environment } from 'environments/environment';

@Injectable()
export class PermisoHttpRepository extends PermisoRepository {
  private http = inject(HttpClient);
  private storage = inject(StoragePort);
  private baseUrl = `${environment.apiUrl}/api/permiso/v1`;

  getMisPermisos(usuarioId?: number): Observable<Result<PermisoEntity, NetworkError>> {
    const empresaId = this.storage.getCurrentUser()?.empresaId;
    let params = new HttpParams().set('empresaId', empresaId ?? '');
    if (usuarioId) {
      params = params.set('usuarioId', usuarioId);
    }

    return this.http.get<any>(`${this.baseUrl}/mis-permisos`, { params }).pipe(
      map((response) => {
        const entity = PermisoEntity.fromPersistence(response.data);
        return Result.ok<PermisoEntity, NetworkError>(entity);
      }),
      catchError((error) =>
        of(Result.fail<PermisoEntity, NetworkError>(NetworkError.fromHttpError(error)))
      )
    );
  }

  getPermisosPorUsuario(usuarioId: number, empresaId: number): Observable<Result<PermisoEntity, NetworkError>> {
    return this.http
      .get<any>(`${this.baseUrl}/usuario/${usuarioId}/empresa/${empresaId}`)
      .pipe(
        map((response) => {
          const entity = PermisoEntity.fromPersistence(response.data);
          return Result.ok<PermisoEntity, NetworkError>(entity);
        }),
        catchError((error) =>
          of(Result.fail<PermisoEntity, NetworkError>(NetworkError.fromHttpError(error)))
        )
      );
  }

  getListado(): Observable<Result<PermisoItemEntity[], NetworkError>> {
    return this.http.get<any>(`${this.baseUrl}/listado`).pipe(
      map((response) => {
        const items: PermisoItemEntity[] = response.data.map(
          (item: any) => PermisoItemEntity.fromPersistence(item)
        );
        return Result.ok<PermisoItemEntity[], NetworkError>(items);
      }),
      catchError((error) =>
        of(Result.fail<PermisoItemEntity[], NetworkError>(NetworkError.fromHttpError(error)))
      )
    );
  }


  asignarAUsuario(usuarioId: number, empresaId: number, permisos: string[]): Observable<Result<void, NetworkError>> {
    return this.http
      .post<any>(`${this.baseUrl}/asignar`, {
        usuarioId: String(usuarioId),
        empresaId: String(empresaId),
        permisos,
      })
      .pipe(
        map(() => Result.ok<void, NetworkError>(undefined)),
        catchError((error) => {
          const backendMessage: string = error?.error?.message ?? error?.message ?? '';
          if (backendMessage.includes('Duplicate entry')) {
            return of(Result.fail<void, NetworkError>(
              new NetworkError('Permiso ya asignado', error?.status ?? 0, error?.url ?? '')
            ));
          }
          return of(Result.fail<void, NetworkError>(NetworkError.fromHttpError(error)));
        })
      );
  }

  eliminar(usuarioId: number, empresaId: number, permiso: string): Observable<Result<void, NetworkError>> {
    return this.http
      .delete<any>(`${this.baseUrl}/eliminar`, {
        body: {
          usuarioId: String(usuarioId),
          empresaId: String(empresaId),
          permiso,
        },
      })
      .pipe(
        map(() => Result.ok<void, NetworkError>(undefined)),
        catchError((error) =>
          of(Result.fail<void, NetworkError>(NetworkError.fromHttpError(error)))
        )
      );
  }
}
